import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { doc, getDoc, setDoc, updateDoc, collection, query, where, Timestamp, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';
import { startOfDay } from "date-fns";
import { translate } from './i18n';

const AppContext = createContext();

export const AppProvider = ({ children }) => {
  const [userId, setUserId] = useState(() => localStorage.getItem('userId'));
  const [userName, setUserName] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [isOnShift, setIsOnShift] = useState(false);
  const [shiftData, setShiftData] = useState(null);
  const [saleTax, setSaleTax] = useState(null);
  const [credit, setCredit] = useState(null);
  const [shopId, setShopId] = useState(null);
  const [useShiftSystem, setUseShiftSystem] = useState(false);
  const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'en');
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Memoized shift status checker
  const checkShiftStatus = useCallback((currentUserId) => {
    const shiftsRef = collection(db, 'shifts');
    const q = query(
      shiftsRef,
      where('userId', '==', currentUserId),
      where('shopId', '==', shopId),
      where('isOnShift', '==', true),
      where('shiftStartTime', '>=', Timestamp.fromDate(startOfDay(new Date())))
    );

    return onSnapshot(q,
      (querySnapshot) => {
        if (!querySnapshot.empty) {
          const doc = querySnapshot.docs[0];
          const activeShift = doc.data();
          setIsOnShift(activeShift.isOnShift);
          setShiftData({ ...activeShift, id: doc.id });
        } else {
          setIsOnShift(false);
          setShiftData(null);
        }
      },
      (error) => {
        console.error('Error listening to shift status:', error);
      }
    );
  }, [shopId]);

  const fetchShopData = useCallback(async (shopId) => {
    try {
     // setIsLoading(true);
      const shopDoc = await getDoc(doc(db, 'shops', shopId));
      if (shopDoc.exists()) {
        const shopData = shopDoc.data();
        setSaleTax(shopData.saleTax);
        setCredit(shopData.credit);
        setUseShiftSystem(shopData.useShiftSystem);
      }
    } catch (error) {
      console.error('Error fetching shop data:', error);
    } finally {
     // setIsLoading(false);
    }
  }, []);

  const initializeUserData = useCallback(async (currentUserId) => {
    try {
     // setIsLoading(true);
      const userDoc = await getDoc(doc(db, 'users', currentUserId));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUserRole(userData.role);
        setUserName(userData.name);
        setShopId(userData.shopId);
        if (userData.shopId) {
          await fetchShopData(userData.shopId);
        }
      }
    } catch (error) {
      console.error('Error initializing user data:', error);
    } finally {
      //setIsLoading(false);
    }
  }, [fetchShopData]);

  // Initialize from local storage
  useEffect(() => {
    let mounted = true;
    const initializeFromLocalStorage = async () => {
     // setIsLoading(true);
      const storedUserId = localStorage.getItem('userId');
      if (storedUserId && !userId && mounted) {
        setUserId(storedUserId);
        await initializeUserData(storedUserId);
        await checkShiftStatus(storedUserId);
      }
      if (mounted) {
        setIsInitialized(true);
      //  setIsLoading(false);
      }
    };
    initializeFromLocalStorage();
    return () => { mounted = false; };
  }, [initializeUserData, checkShiftStatus, userId]);

  // Persist language preference
  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  // Update localStorage when userId changes
  useEffect(() => {
    if (userId) {
      localStorage.setItem('userId', userId);
      initializeUserData(userId);
    }
  }, [userId, initializeUserData]);

  // Setup shift listener
  useEffect(() => {
    let unsubscribe;
    if (userId && shopId) {
      unsubscribe = checkShiftStatus(userId);
    }
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [userId, shopId, checkShiftStatus]);


  const openShift = async (openingCash) => {
    if (isOnShift || !userId || !shopId) return;
    try {
     // setIsLoading(true);
      const newShiftData = {
        userId,
        userName: userName || '',
        shopId,
        isOnShift: true,
        shiftStartTime: new Date(),
        openingCash,
        actualCash: openingCash,
        cashPayments: 0,
        cashDifference: 0,
        paidIn: 0,
        paidOut: 0,
        refunds: 0
      };

      const shiftRef = doc(collection(db, 'shifts'));
      await setDoc(shiftRef, newShiftData);
      setIsOnShift(true);
      setShiftData({ ...newShiftData, id: shiftRef.id });
    } catch (error) {
      console.error('Error opening shift:', error);
      throw error;
    } finally {
      //setIsLoading(false);
    }
  };

  const updateShiftData = async (updates) => {
    //if (!isOnShift || !shiftData?.id) return;
    try {
     // setIsLoading(true);
      const shiftRef = doc(db, 'shifts', shiftData.id);
      await updateDoc(shiftRef, updates);
      setShiftData(prev => prev ? { ...prev, ...updates } : null);
    } catch (error) {
      console.error('Error updating shift:', error);
      throw error;
    } finally {
      //setIsLoading(false);
    }
  };

  const closeShift = async (actualCash, cashPayments, refunds, paidIn, paidOut) => {
   // if (!isOnShift || !shiftData?.id || !shiftData.openingCash) return;
    try {
      const cashDifference = actualCash - (shiftData.openingCash + cashPayments + paidIn - paidOut - refunds);
      
      const updates = {
        isOnShift: false,
        shiftEndTime: new Date(),
        actualCash,
        cashDifference,
        cashPayments,
        refunds,
        paidIn,
        paidOut
      };

      await updateShiftData(updates);
      setIsOnShift(false);
      setShiftData(null);
    } catch (error) {
      console.error('Error closing shift:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const t = useCallback((key, vars = {}) => translate(language, key, vars), [language]);

  // Memoized context value
  const value = useMemo(() => ({
    userId,
    setUserId,
    userName,
    setUserName,
    userRole,
    setUserRole,
    isOnShift,
    shiftData,
    saleTax,
    credit,
    setCredit,
    openShift,
    updateShiftData,
    closeShift,
    shopId,
    setShopId,
    useShiftSystem,
    setUseShiftSystem,
    language,
    setLanguage,
    t,
    isInitialized,
    setIsOnShift,
    isLoading
  }), [
    userId, userName, userRole, isOnShift, shiftData, saleTax, credit,
    shopId, useShiftSystem, language, isInitialized, isLoading, t,
    openShift, updateShiftData, closeShift
  ]);

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

// Custom hook for using context
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

export default AppContext;